'use client'

import { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { subscribeNewsletter } from '@/lib/api'

const columns = [
  {
    title: 'Product',
    links: ['Crypto loans', 'Credit line', 'Business lending', 'Loan calculator'],
  },
  {
    title: 'Company',
    links: ['About', 'Careers', 'Press', 'Blog'],
  },
  {
    title: 'Resources',
    links: ['Help center', 'Proof of reserves', 'Security', 'Status'],
  },
]

export default function Footer() {
  const [email, setEmail] = useState('')
  const [msg, setMsg] = useState('')
  const [loading, setLoading] = useState(false)

  const subscribe = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setMsg('')
    try {
      await subscribeNewsletter(email)
      setMsg('Subscribed! Watch your inbox.')
      setEmail('')
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Subscription failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <footer className="relative border-t border-steel-700/40 bg-ink-soft">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Brand + newsletter */}
          <div className="lg:col-span-5">
            <Link href="/" className="flex items-center gap-3 mb-5">
              <div className="relative h-11 w-11 rounded-xl overflow-hidden ring-1 ring-electric-500/30">
                <Image src="/logo.png" alt="Crypto Credits" fill className="object-cover scale-[1.35]" />
              </div>
              <div className="leading-none">
                <span className="block font-display font-bold tracking-wide text-[15px] text-chrome">
                  CRYPTO CREDITS
                </span>
                <span className="block text-[9px] tracking-[0.35em] text-electric-400/80 mt-1">
                  DIGITAL ASSET LENDING
                </span>
              </div>
            </Link>
            <p className="text-sm text-steel-400 max-w-sm mb-6">
              Instant liquidity against your digital assets. Borrow, hold, and grow —
              without ever selling your crypto.
            </p>

            <form onSubmit={subscribe} className="max-w-sm">
              <label className="block text-xs text-steel-400 mb-2">
                Get market insights & product updates
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="flex-1 px-4 py-2.5 rounded-lg bg-ink border border-steel-700/60 text-white placeholder-steel-500 text-sm focus:border-electric-500 focus:outline-none transition-colors"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-shimmer px-5 py-2.5 rounded-lg font-semibold text-sm bg-gradient-to-b from-electric-400 to-electric-600 text-white shadow-glow-sm disabled:opacity-60"
                >
                  {loading ? '…' : 'Subscribe'}
                </button>
              </div>
              {msg && <p className="text-xs text-electric-300 mt-2">{msg}</p>}
            </form>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {columns.map((c) => (
              <div key={c.title}>
                <h4 className="text-sm font-semibold text-white mb-4">{c.title}</h4>
                <ul className="space-y-3">
                  {c.links.map((l) => (
                    <li key={l}>
                      <a href="#" className="text-sm text-steel-400 hover:text-electric-300 transition-colors">
                        {l}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="neon-line my-10" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-steel-500">
            © {new Date().getFullYear()} Crypto Credits. All rights reserved. Lending
            involves risk; digital assets are volatile.
          </p>
          <div className="flex items-center gap-6 text-xs text-steel-500">
            <a href="#" className="hover:text-steel-300 transition-colors">Terms</a>
            <a href="#" className="hover:text-steel-300 transition-colors">Privacy</a>
            <a href="#" className="hover:text-steel-300 transition-colors">Disclosures</a>
          </div>
        </div>
      </div>
    </footer>
  )
}
